import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import type { Lead } from '@/types/lead'

export type ShortlistStatus =
  | 'active'
  | 'contacted'
  | 'meeting_scheduled'
  | 'proposal_sent'
  | 'negotiating'
  | 'converted'
  | 'dropped'

export interface ShortlistItem {
  id: string
  lead_id: string
  added_by: string | null
  notes: string | null
  priority: number
  status: ShortlistStatus
  next_action: string | null
  next_action_date: string | null
  created_at: string
  updated_at: string
  lead?: Lead
}

export const SHORTLIST_STATUS_LABELS: Record<ShortlistStatus, string> = {
  active: 'Active',
  contacted: 'Contacted',
  meeting_scheduled: 'Meeting Scheduled',
  proposal_sent: 'Proposal Sent',
  negotiating: 'Negotiating',
  converted: 'Converted',
  dropped: 'Dropped',
}

export const SHORTLIST_STATUS_COLORS: Record<ShortlistStatus, string> = {
  active: '#3b82f6',
  contacted: '#8b5cf6',
  meeting_scheduled: '#f59e0b',
  proposal_sent: '#06b6d4',
  negotiating: '#f97316',
  converted: '#22c55e',
  dropped: '#6b7280',
}

// Fetch all shortlisted leads with lead details
export function useShortlist() {
  return useQuery({
    queryKey: ['shortlist'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('lead_shortlist')
        .select(`
          *,
          lead:leads(
            id,
            name,
            slug,
            type,
            status,
            country,
            country_code,
            city,
            latitude,
            longitude,
            image_url,
            website,
            phone,
            email,
            revenue,
            market_share,
            employee_range,
            revenue_range,
            services,
            industry
          )
        `)
        .order('priority', { ascending: false })
        .order('created_at', { ascending: false })
        .limit(10000)

      if (error) throw error
      return data as ShortlistItem[]
    },
  })
}

// Check if a lead is shortlisted
export function useIsShortlisted(leadId: string | undefined) {
  return useQuery({
    queryKey: ['shortlist', 'check', leadId],
    queryFn: async () => {
      if (!leadId) return null

      const { data, error } = await supabase
        .from('lead_shortlist')
        .select('id, status, notes, priority')
        .eq('lead_id', leadId)
        .maybeSingle()

      if (error) throw error
      return data as Pick<ShortlistItem, 'id' | 'status' | 'notes' | 'priority'> | null
    },
    enabled: !!leadId,
  })
}

// Add to shortlist
export function useAddToShortlist() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      lead_id,
      notes,
      priority = 0
    }: {
      lead_id: string
      notes?: string
      priority?: number
    }) => {
      const { data, error } = await supabase
        .from('lead_shortlist')
        .insert({
          lead_id,
          notes,
          priority,
          status: 'active',
        })
        .select()
        .single()

      if (error) throw error
      return data as ShortlistItem
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['shortlist'] })
    },
  })
}

// Remove from shortlist
export function useRemoveFromShortlist() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (leadId: string) => {
      const { error } = await supabase
        .from('lead_shortlist')
        .delete()
        .eq('lead_id', leadId)

      if (error) throw error
      return leadId
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['shortlist'] })
    },
  })
}

// Update shortlist item
export function useUpdateShortlistItem() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      id,
      ...updates
    }: Partial<ShortlistItem> & { id: string }) => {
      const { data, error } = await supabase
        .from('lead_shortlist')
        .update(updates)
        .eq('id', id)
        .select()
        .single()

      if (error) throw error
      return data as ShortlistItem
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['shortlist'] })
    },
  })
}

// Get shortlist count
export function useShortlistCount() {
  return useQuery({
    queryKey: ['shortlist', 'count'],
    queryFn: async () => {
      const { count, error } = await supabase
        .from('lead_shortlist')
        .select('id', { count: 'exact', head: true })
        .neq('status', 'dropped')
        .neq('status', 'converted')

      if (error) throw error
      return count || 0
    },
  })
}
